import React, { useEffect, useState } from "react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/context/AuthContext";
import { X } from "lucide-react";

export default function AuthModal({ open, onClose, defaultMode = "login" }) {
  const { t } = useI18n();
  const { login, register } = useAuth();
  const [mode, setMode] = useState(defaultMode);
  const [form, setForm] = useState({ name: "", email: "", phone: "", password: "" });
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setMode(defaultMode); setErr(""); } }, [open, defaultMode]);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    const r = mode === "login"
      ? await login(form.email, form.password)
      : await register({ name: form.name, email: form.email, phone: form.phone, password: form.password });
    setBusy(false);
    if (!r.ok) setErr(r.error);
    else onClose();
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div data-testid="auth-modal" className="fixed inset-0 z-[80] grid place-items-center p-4">
      <div className="absolute inset-0 bg-onyx/85 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-md bg-charcoal border border-bone/10 p-8 lg:p-10 animate-fade-up">
        <button data-testid="auth-close" onClick={onClose} className="absolute top-4 right-4 text-bone/60 hover:text-brass">
          <X className="w-5 h-5" />
        </button>

        <div className="eyebrow mb-3">{mode === "login" ? "Sign in" : "Sign up"}</div>
        <h3 className="font-display text-3xl text-bone mb-8">
          <span className="italic-display text-brass">
            {mode === "login" ? t.auth.login_title : t.auth.register_title}
          </span>
        </h3>

        <button
          type="button"
          data-testid="auth-google"
          onClick={googleLogin}
          className="w-full flex items-center justify-center gap-3 py-3 border border-bone/15 hover:border-brass hover:text-brass text-bone text-sm tracking-[0.18em] uppercase transition-all duration-500"
        >
          <svg className="w-4 h-4" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 16 19 13 24 13c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.5 6.1 29.5 4 24 4 16.4 4 9.8 8.4 6.3 14.7z"/><path fill="#4CAF50" d="M24 44c5.4 0 10.3-2.1 14-5.4l-6.5-5.5c-2 1.4-4.6 2.4-7.5 2.4-5.2 0-9.6-3.3-11.2-8l-6.6 5.1C9.6 39.5 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.3 5.7l6.5 5.5c-.5.4 6.7-4.9 6.7-15.2 0-1.3-.1-2.4-.6-3.5z"/></svg>
          {t.auth.google}
        </button>

        <div className="my-6 flex items-center gap-3 text-[0.65rem] tracking-[0.32em] uppercase text-taupe">
          <span className="flex-1 h-px bg-bone/10" />
          <span>{t.auth.or}</span>
          <span className="flex-1 h-px bg-bone/10" />
        </div>

        <form onSubmit={submit} className="space-y-2">
          {mode === "register" && (
            <>
              <div>
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.name}</label>
                <input data-testid="auth-name" required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} className="field" />
              </div>
              <div>
                <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.phone}</label>
                <input data-testid="auth-phone" value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} className="field" />
              </div>
            </>
          )}
          <div>
            <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.email}</label>
            <input data-testid="auth-email" required type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} className="field" />
          </div>
          <div>
            <label className="text-[0.65rem] tracking-[0.2em] uppercase text-taupe">{t.auth.password}</label>
            <input data-testid="auth-password" required type="password" minLength={6} value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} className="field" />
          </div>
          {err && <div data-testid="auth-error" className="text-red-400 text-sm pt-2">{err}</div>}
          <button data-testid="auth-submit" type="submit" disabled={busy} className="btn-brass w-full justify-center mt-6">
            {busy ? "..." : mode === "login" ? t.auth.sign_in : t.auth.sign_up}
          </button>
        </form>

        <div className="text-center mt-6 text-sm text-bone/60">
          {mode === "login" ? (
            <>
              {t.auth.no_account}{" "}
              <button data-testid="switch-to-register" onClick={() => setMode("register")} className="text-brass link-under">{t.auth.sign_up}</button>
            </>
          ) : (
            <>
              {t.auth.have_account}{" "}
              <button data-testid="switch-to-login" onClick={() => setMode("login")} className="text-brass link-under">{t.auth.sign_in}</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
